import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { hasAudioInSession } from '@/audio/sessionAudio';
import { formatKey } from '@/core/theory/key';
import { formatRelative, formatTime } from '@/lib/format';
import type { Song } from '@/storage/types';
import { buttonClass } from '@/ui/button';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { Cover } from '@/ui/Cover';
import { querySongs, SORT_OPTIONS, type SongSort } from './query';
import styles from './SongTable.module.css';

interface SongTableProps {
  songs: Song[];
  onOpen: (song: Song) => void;
  onDelete: (song: Song) => void;
}

/** "Your songs" list with search, sort and remove (F7.1, F7.3; design: Library). */
export function SongTable({ songs, onOpen, onDelete }: SongTableProps) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SongSort>('recent');
  const [pendingDelete, setPendingDelete] = useState<Song | null>(null);
  const shown = useMemo(() => querySongs(songs, search, sort), [songs, search, sort]);

  return (
    <section aria-labelledby="songs-heading" className={styles.section}>
      <div className={styles.header}>
        <h2 id="songs-heading" className={styles.heading}>
          This session’s songs
        </h2>
        <span className={styles.count} aria-live="polite">
          {shown.length === songs.length
            ? `${songs.length} ${songs.length === 1 ? 'song' : 'songs'}`
            : `${shown.length} of ${songs.length} songs`}
        </span>
      </div>

      {songs.length === 0 ? (
        <p className={styles.empty}>
          No songs yet in this session. Drop a file above to see its chords.
        </p>
      ) : (
        <>
          <div className={styles.toolbar}>
            <label className={styles.search}>
              <span className={styles.visuallyHidden}>Search your songs</span>
              <input
                type="search"
                placeholder="Search title or artist"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
            </label>
            <label className={styles.sort}>
              Sort by
              <select
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value as SongSort);
                }}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {shown.length === 0 ? (
            <p className={styles.empty}>No songs match. Try another search.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Song</th>
                  <th scope="col">Length</th>
                  <th scope="col">Key</th>
                  <th scope="col">BPM</th>
                  <th scope="col">Last opened</th>
                  <th scope="col">Audio</th>
                  <th scope="col">
                    <span className={styles.visuallyHidden}>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((song) => {
                  // YouTube songs play from their video, so they're always ready.
                  const ready = Boolean(song.youtubeId) || hasAudioInSession(song.id);
                  return (
                    <tr key={song.id}>
                      <td>
                        <div className={styles.songCell}>
                          <Cover title={song.title} src={song.cover} size={44} />
                          <span className={styles.songText}>
                            <span className={styles.songTitle}>{song.title}</span>
                            <span className={styles.songArtist}>
                              {song.artist ??
                                (song.youtubeId ? 'YouTube' : 'No tags — file name shown')}
                            </span>
                          </span>
                        </div>
                      </td>
                      <td>{formatTime(song.duration)}</td>
                      <td>{formatKey(song.summary.key)}</td>
                      <td>{Math.round(song.summary.bpm)}</td>
                      <td>{formatRelative(song.lastOpenedAt)}</td>
                      <td>
                        <span className={styles.badge} data-ready={ready || undefined}>
                          {ready ? 'Ready to play' : 'Select file to play'}
                        </span>
                      </td>
                      <td>
                        <div className={styles.actions}>
                          <Link
                            to="/"
                            onClick={() => {
                              onOpen(song);
                            }}
                            className={buttonClass(ready ? 'primary' : 'secondary', styles.open)}
                            aria-label={`Open ${song.title}`}
                          >
                            Open
                          </Link>
                          <button
                            type="button"
                            className={styles.remove}
                            aria-label={`Remove ${song.title}`}
                            onClick={() => {
                              setPendingDelete(song);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Remove this song?"
        confirmLabel="Remove"
        onCancel={() => {
          setPendingDelete(null);
        }}
        onConfirm={() => {
          if (pendingDelete) onDelete(pendingDelete);
          setPendingDelete(null);
        }}
      >
        <p>
          The chords and settings for <b>{pendingDelete?.title}</b> will be removed from this
          browser. Your audio file isn&apos;t affected; select it again any time to analyze it.
        </p>
      </ConfirmDialog>
    </section>
  );
}
